import { ReactElement, useMemo } from 'react';
import { AlphaBaseDetail, ComponentResult } from '../breakdownMath';
import { formatNumber } from '../breakdownFormat';
import DerivationChain from './DerivationChain';
import TraverseTimeSeries from '../TraverseTimeSeries';

interface Props {
  detail: ComponentResult;
  alphaBaseDetail: AlphaBaseDetail;
  // Pipeline AlphaBase_fG2 = AlphaBase_XG2 × TimeAbsDiff_tG3 — the canonical per-gate
  // integral form computed by the gridded pipeline. This is the value displayed as
  // "AlphaBase" in the derivation chain; the client integration is a verification only.
  pipelineAlphaBaseF: number;
  duration: number;
  ts: number[];
  dynPresG21: number[];
  airYaw: number[];
  whlRevSpd: number[];
  cpCorr: number;
  wheelCircumference: number;
  gateStart: number;
  gateEnd: number;
  // Pipeline reference values for the side-by-side comparison
  legacyAlphaPower: number;        // Alpha_PG3 (legacy primary)
  pipelineAlphaAltPower: number;   // AlphaAlt_PG3 (becoming new primary)
  // ALPHA-DEBUG: piggyback the SessionBreakdown payload through this component so it
  // also gets posted/logged from this render lifecycle.
  debugPayload?: Record<string, unknown>;
}

export default function AlphaSection({
  detail,
  alphaBaseDetail,
  pipelineAlphaBaseF,
  duration,
  ts,
  dynPresG21,
  airYaw,
  whlRevSpd,
  cpCorr,
  wheelCircumference,
  gateStart,
  gateEnd,
  legacyAlphaPower,
  pipelineAlphaAltPower,
  debugPayload,
}: Props): ReactElement {
  // Canonical AlphaBase = pipeline AlphaBase_fG2. AlphaAlt is derived at render time so
  // calibration tweaks (cp_corr, wheel_circumference) take effect immediately.
  const alphaBase = pipelineAlphaBaseF;
  const alphaAltWork = Number.isFinite(alphaBase)
    ? alphaBase * cpCorr * wheelCircumference
    : NaN;
  const alphaAltPower = duration > 0 && Number.isFinite(alphaAltWork)
    ? alphaAltWork / duration : NaN;

  // Client-side independent integration of the three raw gridded inputs (sanity check
  // against the pipeline AlphaBase_fG2; should match within numerical precision).
  const clientAlphaBase = alphaBaseDetail.alphaBase;
  const clientAlphaBaseDelta = Number.isFinite(alphaBase) && Number.isFinite(clientAlphaBase)
    ? clientAlphaBase - alphaBase : NaN;
  const clientAlphaBaseDeltaPct = Number.isFinite(clientAlphaBaseDelta) && alphaBase !== 0
    ? (clientAlphaBaseDelta / alphaBase) * 100 : NaN;

  // Pipeline reference works for the comparison row
  const legacyAlphaWork = Number.isFinite(legacyAlphaPower) ? legacyAlphaPower * duration : NaN;
  const pipelineAlphaAltWork = Number.isFinite(pipelineAlphaAltPower)
    ? pipelineAlphaAltPower * duration : NaN;
  const altVsLegacyDelta = Number.isFinite(pipelineAlphaAltWork) && Number.isFinite(legacyAlphaWork)
    ? pipelineAlphaAltWork - legacyAlphaWork : NaN;
  const altVsLegacyPct = Number.isFinite(altVsLegacyDelta) && legacyAlphaWork !== 0
    ? (altVsLegacyDelta / legacyAlphaWork) * 100 : NaN;
  const clientReconVsPipelineDelta = Number.isFinite(pipelineAlphaAltWork)
    ? alphaAltWork - pipelineAlphaAltWork : NaN;
  const clientReconVsPipelinePct =
    Number.isFinite(clientReconVsPipelineDelta) && pipelineAlphaAltWork !== 0
      ? (clientReconVsPipelineDelta / pipelineAlphaAltWork) * 100 : NaN;

  const cosYawSeries = useMemo(
    () => airYaw.map((y) => (Number.isFinite(y) ? Math.cos((y * Math.PI) / 180) : NaN)),
    [airYaw],
  );

  // ALPHA-DEBUG: mirror the panel-displayed values so they land in the backend log.
  const _alphaDebugSection = {
    label: 'AlphaSection',
    duration,
    cpCorr,
    wheelCircumference,
    pipelineAlphaBaseF,
    alphaBase_used: alphaBase,
    alphaAltWork_reconstructed: alphaAltWork,
    alphaAltPower_reconstructed: alphaAltPower,
    clientAlphaBase_integration: clientAlphaBase,
    clientAlphaBase_delta: clientAlphaBaseDelta,
    clientAlphaBase_delta_pct: clientAlphaBaseDeltaPct,
    pipelineAlphaAltWork_displayed: pipelineAlphaAltWork,
    legacyAlphaWork_displayed: legacyAlphaWork,
    legacyAlphaPower,
    pipelineAlphaAltPower,
    clientRecon_vs_pipeline_delta: clientReconVsPipelineDelta,
    clientRecon_vs_pipeline_pct: clientReconVsPipelinePct,
    altVsLegacyDelta,
    altVsLegacyPct,
    detail_work_legacyComputeAlpha: detail.work,
  };
  // eslint-disable-next-line no-console
  console.log('[ALPHA-DEBUG AlphaSection]', _alphaDebugSection);
  fetch('/data/api/v1.1/debug/log', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(_alphaDebugSection),
  }).catch(() => { /* fire-and-forget */ });
  if (debugPayload) {
    // eslint-disable-next-line no-console
    console.log('[ALPHA-DEBUG SessionBreakdown via AlphaSection]', debugPayload);
    fetch('/data/api/v1.1/debug/log', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...debugPayload, label: 'SessionBreakdown_via_AlphaSection' }),
    }).catch(() => { /* fire-and-forget */ });
  }

  return (
    <div className="cr-py-2">
      <p className="cr-text-sm cr-text-gray-500 cr-mb-4">
        Aerodynamic energy proxy. <strong>AlphaAlt</strong> is the new primary Alpha channel
        (derived at render-time from the calibration-free <code>AlphaBase_fG2</code>, so
        <code> cp_corr</code> and <code>wheel_circumference</code> changes don&apos;t require
        reprocessing). Legacy <code>Alpha_PG3</code> is shown for comparison and is being
        phased out.
      </p>

      {/* Three raw input plots: the building blocks of AlphaBase. */}
      <TraverseTimeSeries
        ts={ts}
        values={dynPresG21}
        gateStart={gateStart}
        gateEnd={gateEnd}
        yLabel="DynPres_pG21 (raw, no cp_corr)"
        unit="Pa"
        title="DynPres_pG21 — raw gridded dynamic pressure"
      />
      <div className="cr-h-3" />
      <TraverseTimeSeries
        ts={ts}
        values={cosYawSeries}
        gateStart={gateStart}
        gateEnd={gateEnd}
        yLabel="cos(AirYaw_AG31)"
        unit=""
        title="cos(AirYaw_AG31) — yaw projection onto road direction"
      />
      <div className="cr-h-3" />
      <TraverseTimeSeries
        ts={ts}
        values={whlRevSpd}
        gateStart={gateStart}
        gateEnd={gateEnd}
        yLabel="WhlRspd_n"
        unit="rev/s"
        title="WhlRspd_n — wheel angular speed (revolutions per second)"
      />

      <div className="cr-mt-4">
        <DerivationChain
          steps={[
            {
              label: '∫ (DynPres_pG21 × cos(AirYaw_AG31) × WhlRspd_n) dt over gate',
              value: 'AlphaBase_fG2 (from pipeline)',
            },
            {
              label: '= AlphaBase',
              value: Number.isFinite(alphaBase) ? formatNumber(alphaBase, 2) : 'n/a',
              isResult: false,
            },
            { label: '× cp_corr', value: formatNumber(cpCorr, 4) },
            { label: '× wheel_circumference', value: `${formatNumber(wheelCircumference, 4)} m` },
            {
              label: '= AlphaAlt (new primary Alpha, work)',
              value: Number.isFinite(alphaAltWork) ? `${formatNumber(alphaAltWork, 0)} J` : 'n/a',
              isResult: true,
            },
            { label: '÷ Time', value: `${formatNumber(duration, 2)} s` },
            {
              label: '= AlphaAlt power',
              value: Number.isFinite(alphaAltPower) ? `${formatNumber(alphaAltPower, 1)} W` : 'n/a',
              isResult: true,
            },
          ]}
        />
      </div>

      <div className="cr-mt-3 cr-pt-3 cr-border-t cr-border-gray-200 cr-text-sm">
        <div className="cr-text-gray-700 cr-font-medium cr-mb-1">
          Pipeline comparison (per-traverse work, J)
        </div>
        <div className="cr-grid cr-grid-cols-3 cr-gap-3 cr-font-mono cr-text-gray-700">
          <div>
            <div className="cr-text-xs cr-text-gray-500">
              Client AlphaAlt (AlphaBase_fG2 × cp_corr × C)
            </div>
            <div>
              {Number.isFinite(alphaAltWork) ? `${formatNumber(alphaAltWork, 0)} J` : 'n/a'}
            </div>
          </div>
          <div>
            <div className="cr-text-xs cr-text-gray-500">
              Pipeline AlphaAlt_PG3 × ΔT
            </div>
            <div>
              {Number.isFinite(pipelineAlphaAltWork)
                ? `${formatNumber(pipelineAlphaAltWork, 0)} J` : 'n/a'}
              {Number.isFinite(clientReconVsPipelinePct) && (
                <span className={`cr-ml-2 cr-text-xs ${
                  Math.abs(clientReconVsPipelinePct) > 0.5
                    ? 'cr-text-amber-700' : 'cr-text-gray-500'
                }`}>
                  (client Δ {clientReconVsPipelinePct >= 0 ? '+' : ''}
                  {formatNumber(clientReconVsPipelinePct, 2)}%)
                </span>
              )}
            </div>
          </div>
          <div>
            <div className="cr-text-xs cr-text-gray-500">
              Legacy Alpha_PG3 × ΔT (deprecated)
            </div>
            <div>
              {Number.isFinite(legacyAlphaWork) ? `${formatNumber(legacyAlphaWork, 0)} J` : 'n/a'}
              {Number.isFinite(altVsLegacyPct) && (
                <span className={`cr-ml-2 cr-text-xs ${
                  Math.abs(altVsLegacyPct) > 0.5 ? 'cr-text-amber-700' : 'cr-text-gray-500'
                }`}>
                  (AlphaAlt Δ {altVsLegacyPct >= 0 ? '+' : ''}{formatNumber(altVsLegacyPct, 2)}%)
                </span>
              )}
            </div>
          </div>
        </div>

        {Number.isFinite(clientAlphaBase) && (
          <div className="cr-mt-2 cr-text-xs cr-text-gray-500 cr-italic">
            Client-side independent ∫ over the three gridded inputs:{' '}
            <code>{formatNumber(clientAlphaBase, 2)}</code>
            {Number.isFinite(clientAlphaBaseDeltaPct) && (
              <span className={
                Math.abs(clientAlphaBaseDeltaPct) > 0.5 ? 'cr-text-amber-700' : ''
              }>
                {' '}(Δ vs pipeline AlphaBase_fG2:{' '}
                {clientAlphaBaseDeltaPct >= 0 ? '+' : ''}
                {formatNumber(clientAlphaBaseDeltaPct, 2)}%)
              </span>
            )}
          </div>
        )}
      </div>

      <p className="cr-text-xs cr-text-gray-500 cr-mt-3 cr-italic">
        Pipeline reference: <code>cda_post_processing.py</code>:208-210 builds{' '}
        <code>AlphaBase = Σ DynPres_pG21 × cos(AirYaw_AG31) × Δrev</code> over the grid,
        stored as <code>AlphaBase_XG2</code>.{' '}
        <code>cdacalcterms.py</code>:272 produces{' '}
        <code>AlphaBase_fG2 = AlphaBase_XG2 × TimeAbsDiff_tG3</code> (per-gate integral form
        used here). Reconstruction:{' '}
        <code>AlphaAlt_work = AlphaBase_fG2 × cp_corr × wheel_circumference</code>. Live{' '}
        <code>cp_corr</code> (configured, matches pipeline) = {formatNumber(cpCorr, 4)},{' '}
        <code>wheel_circumference</code> = {formatNumber(wheelCircumference, 4)} m.
      </p>
    </div>
  );
}
